/* ITER239 · Phase 3 · Part B — Petty Cash Reports.
 *
 * Visible to BOTH admins and managers, scope-enforced server-side:
 *   • manager → outlet locked to their assignment; tenant rollup forbidden
 *   • admin   → outlet selector + "All outlets" tenant rollup
 *
 * Spend = approved (disbursed) only. Holds/pending never count.
 * Drill-down expands a cell to the underlying vouchers and asserts
 * subtotal equality client-side as a sanity check.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  ArrowLeft, BarChart3, ChevronRight, Download, Layers, Loader2, RefreshCw, Wallet,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_ROLES = new Set(['admin', 'super_admin', 'area_manager']);
const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const PERIODS = [
  { value: 'mtd', label: 'Month to date' },
  { value: 'qtd', label: 'Quarter to date' },
  { value: 'ytd', label: 'Year to date' },
];

export default function PettyCashReports() {
  const { user, getHeaders } = useAuth();
  const isAdmin = ADMIN_ROLES.has(user?.role || '');
  const [period, setPeriod] = useState('mtd');
  const [outlets, setOutlets] = useState([]);
  // For managers, the outlet is locked to their own assignment. Default
  // the pill to the user's actual outlet (not just outlets[0]) so the
  // label always matches the data being scoped.
  const managerOutletDefault = useMemo(() => {
    const assigned = user?.assigned_outlet_ids || (user?.outlet_id ? [user.outlet_id] : []);
    return assigned[0] || '';
  }, [user]);
  const [outletId, setOutletId] = useState(isAdmin ? 'all' : managerOutletDefault);
  const [spend, setSpend] = useState(null);
  const [cashOnHand, setCashOnHand] = useState(null);
  const [pendingRollup, setPendingRollup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drillKey, setDrillKey] = useState(null);  // `${categoryId}|${wallet}`
  const [drillData, setDrillData] = useState(null);
  const [drillBusy, setDrillBusy] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setDrillKey(null);
    setDrillData(null);
    try {
      const outletsPath = isAdmin ? `${API}/admin/outlets` : `${API}/outlets`;
      const [or, sr, cr, pr] = await Promise.all([
        axios.get(outletsPath, { headers: getHeaders() }).catch(() => ({ data: [] })),
        axios.get(`${API}/petty-cash/reports/spend`, {
          headers: getHeaders(),
          params: { period, outlet_id: outletId || (isAdmin ? 'all' : undefined) },
        }),
        axios.get(`${API}/petty-cash/reports/cash-on-hand`, {
          headers: getHeaders(),
          params: outletId && outletId !== 'all' ? { outlet_id: outletId } : {},
        }),
        isAdmin
          ? axios.get(`${API}/petty-cash/reports/pending-transfers`, { headers: getHeaders() })
              .catch(() => ({ data: { count: 0, total: 0, transfers: [] } }))
          : Promise.resolve({ data: null }),
      ]);
      setOutlets(or.data || []);
      setSpend(sr.data);
      setCashOnHand(cr.data);
      setPendingRollup(pr.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load reports');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, period, outletId, getHeaders]);

  useEffect(() => { reload(); }, [reload]);

  const outletNameById = useMemo(() => {
    const map = {};
    for (const o of outlets) map[o.id] = o.name;
    return map;
  }, [outlets]);

  const openDrill = async (categoryId, walletType) => {
    const key = `${categoryId}|${walletType}`;
    if (drillKey === key) { setDrillKey(null); setDrillData(null); return; }
    setDrillKey(key);
    setDrillBusy(true);
    try {
      const r = await axios.get(`${API}/petty-cash/reports/spend/drill`, {
        headers: getHeaders(),
        params: {
          period,
          outlet_id: outletId || (isAdmin ? 'all' : undefined),
          category_id: categoryId,
          wallet_type: walletType,
        },
      });
      setDrillData(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load drill-down');
      setDrillKey(null);
    } finally {
      setDrillBusy(false);
    }
  };

  const back = isAdmin ? '/admin/petty-cash' : '/manager/petty-cash';

  return (
    <div className="space-y-4" data-testid="petty-cash-reports-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Link to={back} className="inline-flex items-center gap-1 text-[12px] text-[#5C6B64] hover:text-[#1A2421]" data-testid="reports-back-link">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to vouchers
          </Link>
          <div>
            <h1 className="text-lg sm:text-xl font-semibold tracking-tight text-[#1A2421]">Petty Cash · Reports</h1>
            <p className="text-[12px] text-[#5C6B64]">Spend by category × wallet · cash on hand · drill into approved vouchers</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="h-8 text-[12px] w-[160px] bg-white border-[#E2E8DF]" data-testid="period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PERIODS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {(isAdmin || outlets.length > 1) && (
            <Select value={outletId || (isAdmin ? 'all' : managerOutletDefault)} onValueChange={setOutletId}>
              <SelectTrigger className="h-8 text-[12px] w-[200px] bg-white border-[#E2E8DF]" data-testid="reports-outlet-select">
                <SelectValue placeholder="Outlet" />
              </SelectTrigger>
              <SelectContent>
                {isAdmin && <SelectItem value="all">All outlets (tenant rollup)</SelectItem>}
                {outlets.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Button variant="outline" onClick={reload}
            className="h-8 text-[12px] gap-1 border-[#E2E8DF] text-[#1A2421]" data-testid="reload-reports">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </Button>
          <ExportReportButton
            outletId={outletId}
            isAdmin={isAdmin}
            getHeaders={getHeaders} />
        </div>
      </div>

      {loading ? (
        <div className="text-[12px] text-[#5C6B64]">Loading…</div>
      ) : (
        <>
          {/* Cash on hand */}
          <Card className="border-[#E2E8DF]" data-testid="cash-on-hand-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-[#1A2421] flex items-center gap-2">
                <Wallet className="w-4 h-4 text-[color:var(--color-primary)]" /> Cash on hand
                {spend?.scope === 'tenant'
                  ? <span className="text-[10px] text-[#5C6B64] font-normal">— tenant-wide</span>
                  : <span className="text-[10px] text-[#5C6B64] font-normal">— {outletNameById[spend?.outlet_id] || ''}</span>}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2">
                {(cashOnHand?.wallets || []).map(w => (
                  <div key={`${w.outlet_id || 'admin'}-${w.wallet_type}`}
                    className="rounded border border-[#E2E8DF] bg-white px-3 py-2"
                    data-testid={`coh-${w.wallet_type}-${w.outlet_id || 'admin'}`}>
                    <div className="text-[10px] uppercase tracking-wider text-[#5C6B64] flex items-center justify-between">
                      <span>{w.wallet_type === 'admin'
                        ? 'Admin central'
                        : `${outletNameById[w.outlet_id] || ''} · ${w.wallet_type.toUpperCase()}`}</span>
                    </div>
                    <div className="text-[18px] font-semibold tabular-nums text-[#1A2421] mt-0.5">{fmt(w.balance)}</div>
                    <div className="text-[10px] text-[#5C6B64]">Current Balance</div>
                  </div>
                ))}
                {(!cashOnHand?.wallets?.length) && (
                  <p className="text-[12px] text-[#5C6B64]">No wallets in scope.</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Pending transfer rollup (admin only) */}
          {isAdmin && pendingRollup && (
            <Card className="border-amber-200 bg-amber-50/40" data-testid="pending-rollup-card">
              <CardContent className="py-3 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2 text-[12px]">
                  <Layers className="w-4 h-4 text-amber-700" />
                  <span className="font-semibold text-amber-900">
                    {pendingRollup.count} pending sales-cash transfer{pendingRollup.count === 1 ? '' : 's'}
                  </span>
                  <span className="text-amber-900 tabular-nums">· total {fmt(pendingRollup.total)}</span>
                </div>
                <Link to="/admin/petty-cash/wallets"
                  className="text-[11px] inline-flex items-center gap-1 text-amber-900 hover:underline"
                  data-testid="pending-rollup-link">
                  Open approval queue <ChevronRight className="w-3 h-3" />
                </Link>
              </CardContent>
            </Card>
          )}

          {/* Spend matrix */}
          <Card className="border-[#E2E8DF]" data-testid="spend-matrix-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-[#1A2421] flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-[color:var(--color-primary)]" /> Spend by category × wallet
                <span className="text-[10px] text-[#5C6B64] font-normal">
                  · {spend?.range?.start} → {spend?.range?.end}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {spend?.rows?.length ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-[12px]" data-testid="spend-table">
                    <thead>
                      <tr className="text-left border-b border-[#E2E8DF] text-[10px] uppercase tracking-wider text-[#5C6B64]">
                        <th className="py-1.5 pr-3 font-medium">Category</th>
                        <th className="py-1.5 px-2 font-medium text-right">Operations</th>
                        <th className="py-1.5 px-2 font-medium text-right">Store</th>
                        <th className="py-1.5 px-2 font-medium text-right">Admin</th>
                        <th className="py-1.5 pl-2 font-medium text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {spend.rows.map(r => (
                        <React.Fragment key={r.category_id}>
                          <tr className="border-b border-[#F0F2EE] hover:bg-[#F9F9F8]" data-testid={`spend-row-${r.category_id}`}>
                            <td className={`py-1.5 pr-3 ${r.is_legacy ? 'text-[#5C6B64] italic' : 'text-[#1A2421]'}`}>
                              {r.category_label}
                            </td>
                            <td className="py-1.5 px-2 text-right tabular-nums">
                              <SpendCell amount={r.ops}
                                onClick={() => r.ops > 0 && openDrill(r.category_id, 'ops')}
                                active={drillKey === `${r.category_id}|ops`}
                                testid={`spend-cell-${r.category_id}-ops`} />
                            </td>
                            <td className="py-1.5 px-2 text-right tabular-nums">
                              <SpendCell amount={r.store}
                                onClick={() => r.store > 0 && openDrill(r.category_id, 'store')}
                                active={drillKey === `${r.category_id}|store`}
                                testid={`spend-cell-${r.category_id}-store`} />
                            </td>
                            <td className="py-1.5 px-2 text-right tabular-nums">
                              <SpendCell amount={r.admin}
                                onClick={() => r.admin > 0 && openDrill(r.category_id, 'admin')}
                                active={drillKey === `${r.category_id}|admin`}
                                testid={`spend-cell-${r.category_id}-admin`} />
                            </td>
                            <td className="py-1.5 pl-2 text-right font-semibold tabular-nums text-[#1A2421]">{fmt(r.total)}</td>
                          </tr>
                          {drillKey && drillKey.startsWith(`${r.category_id}|`) && (
                            <tr className="bg-[#F4F6F2]">
                              <td colSpan={5} className="px-3 py-2" data-testid="spend-drill-block">
                                {drillBusy ? (
                                  <div className="text-[11px] text-[#5C6B64] flex items-center gap-1">
                                    <Loader2 className="w-3 h-3 animate-spin" /> Loading vouchers…
                                  </div>
                                ) : drillData ? (
                                  <DrillTable data={drillData} cellTotal={
                                    drillKey.endsWith('|ops') ? r.ops
                                    : drillKey.endsWith('|store') ? r.store : r.admin
                                  } outletNameById={outletNameById} />
                                ) : null}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                      <tr className="border-t-2 border-[#1A2421] text-[#1A2421] font-semibold text-[12px]">
                        <td className="py-2 pr-3">Total</td>
                        <td className="py-2 px-2 text-right tabular-nums">{fmt(spend.totals.ops)}</td>
                        <td className="py-2 px-2 text-right tabular-nums">{fmt(spend.totals.store)}</td>
                        <td className="py-2 px-2 text-right tabular-nums">{fmt(spend.totals.admin)}</td>
                        <td className="py-2 pl-2 text-right tabular-nums">{fmt(spend.totals.grand)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-[12px] text-[#5C6B64]" data-testid="spend-empty">
                  No approved spend in this period.
                </p>
              )}
              <p className="text-[10px] text-[#5C6B64] mt-2">
                Approved (disbursed) vouchers only. Holds and pending vouchers are excluded — they don&apos;t represent cash that left a wallet.
              </p>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function SpendCell({ amount, onClick, active, testid }) {
  if (!amount) return <span className="text-[#9AA39E]">—</span>;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-1.5 py-0.5 rounded text-[12px] tabular-nums transition ${active
        ? 'bg-[color:var(--color-primary)] text-white'
        : 'text-[#1A2421] hover:bg-[color:var(--color-primary)]/[0.08]'}`}>
      {fmt(amount)}
    </button>
  );
}

function DrillTable({ data, cellTotal, outletNameById }) {
  const subtotalMatchesCell = Math.abs((data.subtotal || 0) - (cellTotal || 0)) < 0.01;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-[11px]">
        <span className="text-[#5C6B64]">
          {data.vouchers.length} voucher{data.vouchers.length === 1 ? '' : 's'} ·
          <strong className={`ml-1 ${subtotalMatchesCell ? 'text-emerald-700' : 'text-rose-700'}`}>
            Subtotal {fmt(data.subtotal)}
          </strong>
          {!subtotalMatchesCell && (
            <span className="text-rose-700 ml-1">(does not match cell — refresh the report)</span>
          )}
        </span>
      </div>
      {data.vouchers.length ? (
        <table className="w-full text-[11px]" data-testid="drill-table">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-[#5C6B64] border-b border-[#E2E8DF]">
              <th className="py-1 pr-2 font-medium">Voucher</th>
              <th className="py-1 px-2 font-medium">Date</th>
              <th className="py-1 px-2 font-medium">Outlet</th>
              <th className="py-1 px-2 font-medium">Paid to</th>
              <th className="py-1 px-2 font-medium">Approver</th>
              <th className="py-1 pl-2 font-medium text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {data.vouchers.map(v => (
              <tr key={v.id} className="border-b border-[#F0F2EE]" data-testid={`drill-voucher-${v.id}`}>
                <td className="py-1 pr-2 font-mono text-[10px] text-[#5C6B64]">{v.voucher_no || v.id.slice(0, 8)}</td>
                <td className="py-1 px-2 text-[#5C6B64] tabular-nums">{(v.disbursed_at || '').slice(0, 10)}</td>
                <td className="py-1 px-2 text-[#1A2421]">{v.outlet_name || outletNameById[v.outlet_id] || ''}</td>
                <td className="py-1 px-2 text-[#1A2421]">{v.paid_to}</td>
                <td className="py-1 px-2 text-[#5C6B64]">{v.approved_by?.name || '—'}</td>
                <td className="py-1 pl-2 text-right tabular-nums text-[#1A2421] font-semibold">{fmt(v.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="text-[11px] text-[#5C6B64]">No vouchers behind this cell.</p>
      )}
    </div>
  );
}



// ─── ITER314 · Download Report (XLSX) ────────────────────────────────
const EXPORT_PERIODS = [
  { value: 'current_month',  label: 'Current Month' },
  { value: 'previous_month', label: 'Previous Month' },
  { value: 'last_3m',        label: 'Last 3 Months' },
  { value: 'last_6m',        label: 'Last 6 Months' },
  { value: 'fy',             label: 'Financial Year (Apr–Mar)' },
  { value: 'custom',         label: 'Custom Range' },
];

function ExportReportButton({ outletId, isAdmin, getHeaders }) {
  const [open, setOpen] = useState(false);
  const [period, setPeriod] = useState('current_month');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [busy, setBusy] = useState(false);

  const download = async () => {
    if (period === 'custom' && (!start || !end)) {
      toast.error('Pick both start and end dates');
      return;
    }
    setBusy(true);
    try {
      const params = new URLSearchParams({ period });
      if (period === 'custom') { params.set('start', start); params.set('end', end); }
      if (outletId && outletId !== 'all') params.set('outlet_id', outletId);
      else if (isAdmin) params.set('outlet_id', 'all');
      const res = await axios.get(`${API}/petty-cash/reports/export.xlsx?${params.toString()}`, {
        headers: getHeaders(),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const cd = res.headers['content-disposition'] || '';
      const fnameMatch = cd.match(/filename="?([^"]+)"?/);
      link.download = fnameMatch ? fnameMatch[1] : `petty_cash_${period}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded');
      setOpen(false);
    } catch (err) {
      // Blob error responses need special decoding.
      let detail = 'Could not download report';
      try {
        const txt = await err.response?.data?.text?.();
        if (txt) detail = JSON.parse(txt)?.detail || detail;
      } catch (_) { /* ignore */ }
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)}
        className="h-8 text-[12px] gap-1 border-[#E2E8DF] text-[#1A2421]"
        data-testid="open-export-report">
        <Download className="w-3.5 h-3.5" /> Download Report
      </Button>
      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          onClick={() => !busy && setOpen(false)}
          data-testid="export-report-dialog">
          <div className="bg-white rounded-lg border border-[#E2E8DF] p-5 w-full max-w-md space-y-3"
            onClick={(e) => e.stopPropagation()}>
            <div>
              <h3 className="text-sm font-semibold text-[#1A2421]">Download Petty Cash Ledger</h3>
              <p className="text-[11px] text-[#5C6B64] mt-0.5">
                Excel (.xlsx) with every ledger transaction — opening balance,
                vouchers, transfers, replenishments and amendments — hydrated
                with voucher #, narration, vendor, approver and audit fields.
              </p>
            </div>
            <div className="space-y-1.5">
              <label className="text-[11px] text-[#5C6B64]">Period</label>
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="h-9 text-[12px] bg-[#F9F9F8] border-[#E2E8DF]" data-testid="export-period">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EXPORT_PERIODS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {period === 'custom' && (
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <label className="text-[11px] text-[#5C6B64]">Start</label>
                  <input type="date" value={start} onChange={(e) => setStart(e.target.value)}
                    className="h-9 w-full rounded border border-[#E2E8DF] bg-[#F9F9F8] px-2 text-[12px]"
                    data-testid="export-start" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] text-[#5C6B64]">End</label>
                  <input type="date" value={end} onChange={(e) => setEnd(e.target.value)}
                    className="h-9 w-full rounded border border-[#E2E8DF] bg-[#F9F9F8] px-2 text-[12px]"
                    data-testid="export-end" />
                </div>
              </div>
            )}
            <p className="text-[10px] text-[#5C6B64] leading-snug">
              Scope: <strong className="text-[#1A2421]">
                {outletId === 'all' || !outletId ? (isAdmin ? 'All outlets' : 'My outlet') : 'Selected outlet'}
              </strong>. Filter by wallet / department / status can be applied via the URL —
              current build downloads the full scoped ledger.
            </p>
            <div className="flex items-center justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => !busy && setOpen(false)}
                className="h-8 text-[12px] border-[#E2E8DF] text-[#5C6B64]"
                data-testid="export-cancel">Cancel</Button>
              <Button disabled={busy} onClick={download}
                className="h-8 text-[12px] bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white gap-1"
                data-testid="export-submit">
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                Download
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
